import { CURATED_FRESHNESS_DAYS, fmtDuration, fmtUtcTime, plural } from '@remote-medium/shared';
import { cn } from '@remote-medium/ui';
import { CalendarClock, ListChecks, Mail, type LucideIcon } from 'lucide-react';
import { Link } from 'react-router';

import { H2, VIEW_ALL } from '../../../lib/ui.tsx';
import type { Overview } from './context.ts';

type JobRun = NonNullable<Overview['jobs']['rank']>;
type Counts = JobRun['counts'];

interface JobView {
  body: string;
  meta: string;
  failed?: boolean;
}

/** Shared shape for a job column: missing, failed, or `ok` copy from its counts. */
function runView(run: JobRun | null, ok: (counts: Counts) => string): JobView {
  if (!run) return { body: 'No run recorded yet.', meta: '—' };
  const meta = `${fmtUtcTime(run.startedAt)} · ${fmtDuration(run.startedAt, run.finishedAt) ?? 'running'}`;
  if (run.status === 'failed') return { body: run.error ?? 'Failed.', meta, failed: true };
  return { body: ok(run.counts), meta };
}

function rankCopy(c: Counts) {
  const roles = plural(c['roles'] ?? 0, 'open role');
  // Runs recorded before `candidates`/`newMatches` existed fall back to pair
  // counts.
  if (c['candidates'] === undefined || c['newMatches'] === undefined)
    return `${plural(c['considered'] ?? 0, 'candidate check')} across ${roles}. ${plural(c['written'] ?? 0, 'match', 'matches')} above threshold.`;
  return `${plural(c['candidates'], 'active candidate')} against ${roles}. ${plural(c['newMatches'], 'new match', 'new matches')} above threshold.`;
}

function freshnessCopy(c: Counts) {
  const n = c['stale'] ?? 0;
  return n
    ? `${plural(n, 'curated listing')} not re-verified in ${CURATED_FRESHNESS_DAYS} days.`
    : `Every curated listing was verified in the last ${CURATED_FRESHNESS_DAYS} days.`;
}

function emailView({ consentRequests, introductions, failed }: Overview['emailsLast24h']): JobView {
  return {
    body: `${plural(consentRequests, 'consent request')} and ${plural(introductions, 'introduction')} sent. ${failed ? `${failed} failed.` : 'None failed.'}`,
    meta: 'Last 24h',
    failed: failed > 0,
  };
}

function JobColumn({
  icon: Icon,
  title,
  body,
  meta,
  failed,
}: JobView & { icon: LucideIcon; title: string }) {
  return (
    <div className="flex flex-col gap-1.5 border-t border-border pt-4">
      <div className="flex items-center gap-2">
        <Icon aria-hidden="true" className="size-4 text-muted-foreground" />
        <span className="text-sm font-semibold">{title}</span>
      </div>
      <p
        className={cn(
          'm-0 text-sm text-pretty',
          failed ? 'text-destructive' : 'text-muted-foreground',
        )}
      >
        {body}
      </p>
      <span className="text-xs text-muted-foreground tabular-nums">{meta}</span>
    </div>
  );
}

export function LatestJobs({
  jobs,
  emails,
}: {
  jobs: Overview['jobs'];
  emails: Overview['emailsLast24h'];
}) {
  return (
    <section className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-4">
        <h2 className={H2}>Latest jobs</h2>
        <Link to="/ops" className={VIEW_ALL}>
          Open job runs
        </Link>
      </div>
      <div className="grid gap-6 md:grid-cols-3">
        <JobColumn icon={ListChecks} title="Ranking" {...runView(jobs.rank, rankCopy)} />
        <JobColumn
          icon={CalendarClock}
          title="Freshness"
          {...runView(jobs.freshness, freshnessCopy)}
        />
        <JobColumn icon={Mail} title="Email" {...emailView(emails)} />
      </div>
    </section>
  );
}
